import type {
  FormTemplateTarget,
  OrderTypeId,
} from '../services/api/types.gen';

/**
 * Everything that differs between the kinds of thing a text template can be
 * written for. The Admin screen and the order picker read their differences
 * from here, so adding a target means adding one entry.
 */
export interface TemplateTargetConfig {
  /** The Admin section that lists these templates. */
  sectionId: 'form-templates' | 'physician-order-templates';
  title: string;
  description: string;
  searchPlaceholder: string;
  /**
   * Templates are written for one visit form field: the Form field column, the
   * form and field picker, and the field shown while editing.
   */
  showsField: boolean;
  /** Templates can be exact format or guidance: the Use column and the toggle. */
  showsKind: boolean;
  /** Order types whose Order Text offers these templates. */
  orderTypes: readonly OrderTypeId[];
}

export const TEMPLATE_TARGET_CONFIG: Record<
  FormTemplateTarget,
  TemplateTargetConfig
> = {
  form_question: {
    sectionId: 'form-templates',
    title: 'Text Templates',
    description:
      'Standard wording clinicians can load into a visit form text box instead of typing it out. A template applies wherever its field appears.',
    searchPlaceholder: 'Search by name or field',
    showsField: true,
    showsKind: true,
    orderTypes: [],
  },
  physician_order: {
    sectionId: 'physician-order-templates',
    title: 'Physician Order Templates',
    description:
      'Standard wording clinicians can load into the Order Text of a Physician Order. These are separate from the Text Templates on the discharge question "Describe other physician orders".',
    searchPlaceholder: 'Search by name',
    showsField: false,
    showsKind: false,
    orderTypes: ['PHYSICIAN_ORDER'],
  },
};

const TEMPLATE_TARGETS = Object.keys(
  TEMPLATE_TARGET_CONFIG
) as FormTemplateTarget[];

/** The template target whose wording an order of this type offers, if any. */
export function templateTargetForOrderType(
  orderType: string
): FormTemplateTarget | null {
  return (
    TEMPLATE_TARGETS.find(target =>
      (TEMPLATE_TARGET_CONFIG[target].orderTypes as readonly string[]).includes(
        orderType
      )
    ) ?? null
  );
}
